import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { MyAction } from '@cortex/domain-org-issues';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { isOverdue, localToday } from '../../../lib/org-issues.js';

/**
 * 自分が担当の未完了 Next（ボード横断）。並びは API の返した順（期日の近い順）のまま。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分が担当の未完了 Next をボード横断で期日の近い順に並べ、期日超過を目立たせ、その課題を開く入口にする
 * @graph-connects isOverdue [calls] 期日超過の判定
 */
export function MyActionsList({ actions }: { actions: readonly MyAction[] }): ReactNode {
  const { t } = useTranslation();
  const today = localToday();
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('orgIssues.myActions.heading')}
        </h2>
        {actions.length === 0 && (
          <p className="text-sm text-[var(--color-text-muted)]">{t('orgIssues.myActions.empty')}</p>
        )}
        <ul className="divide-y divide-[var(--color-border)]">
          {actions.map(({ board, issue, action }) => (
            <li key={`${board.id}/${issue.id}/${action.id}`} className="py-2">
              <Link
                to="/org-issues/$boardId"
                params={{ boardId: board.id }}
                search={{ issue: issue.id }}
                className="block space-y-1 hover:opacity-80"
              >
                <p className="text-sm text-[var(--color-text-primary)]">{action.text}</p>
                <p className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
                  <span>
                    {board.name} / {issue.title}
                  </span>
                  {action.dueOn === null ? (
                    <span>{t('orgIssues.myActions.noDue')}</span>
                  ) : (
                    <span>{action.dueOn}</span>
                  )}
                  {isOverdue(action.dueOn, today) && (
                    <Badge variant="error" size="sm">
                      {t('orgIssues.myActions.overdue')}
                    </Badge>
                  )}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
